import * as Sentry from "@sentry/react";
import {
  flushTranslationsCache,
  useInternationalisation,
} from "api/internationalisation";
import { useAppSelector } from "app/hooks";
import React from "react";
import { IntlProvider } from "react-intl";
import { selectCustomLanguageData } from "reduxState/customLanguageData/customLanguageDataSlice";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { I18nContext, I18nSettings } from "./I18nContext";

type Props = {
  children: React.ReactNode;
};

const getBrowsersI18nSettings = (): I18nSettings => {
  const preferedLanguages = (navigator.languages || [
    navigator.language,
  ]) as string[];

  return {
    language: preferedLanguages[0],
    preferedLanguages,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

let errors: string[] = [];
let timerId: any;
const SESSION_REPORT_KEY_PREFIX = "missing-translations-reported-";

const debouncedLogger =
  (translationsReady: any, locale: string) => (err: any) => {
    if (!translationsReady) return;
    errors.push(err.descriptor.id);
    clearTimeout(timerId);

    timerId = setTimeout(() => {
      const sessionKey = SESSION_REPORT_KEY_PREFIX + locale;
      const hasReportedThisLocale =
        sessionStorage.getItem(sessionKey) === "true";

      if (!hasReportedThisLocale && errors.length > 0) {
        const uniqueErrors = Array.from(new Set(errors));

        Sentry.captureMessage("Missing translation keys", {
          level: "warning",
          tags: {
            type: "missing_translations",
          },
          extra: {
            locale: locale,
            missingKeys: uniqueErrors,
          },
        });

        // Mark as reported for this locale in the current session
        sessionStorage.setItem(sessionKey, "true");
      }

      errors = [];
    }, 200);
  };

export function I18nProvider({ children }: Props) {
  const { useTranslation } = useInternationalisation();
  const customLanguage = useAppSelector(selectCustomLanguageData)?.code;
  const defaultLang = useAppSelector(selectDefaultLanguageData);
  const { data, mutate } = useTranslation(customLanguage);
  const translations = data || {};

  const refetchLanguages = () => {
    flushTranslationsCache();
    mutate && mutate();
  };
  const fallbackLocale = getBrowsersI18nSettings().language;
  const locale = customLanguage || defaultLang.code || fallbackLocale;

  return (
    <I18nContext.Provider value={{ translations, refetchLanguages }}>
      <IntlProvider
        locale={locale}
        messages={translations}
        onError={debouncedLogger(Object.keys(translations).length > 0, locale)}
      >
        {children}
      </IntlProvider>
    </I18nContext.Provider>
  );
}
